import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
	automaticDiscountsApi,
	type AutomaticDiscountsFilter,
	type CreateAutomaticDiscountInput,
	type UpdateAutomaticDiscountInput,
} from "./automatic-discounts-api";

export const KEY_AUTOMATIC_DISCOUNTS = ["automatic-discounts"];

export function useAutomaticDiscountsList(
	filter: AutomaticDiscountsFilter = {},
	enabled = true,
) {
	return useQuery({
		queryKey: [...KEY_AUTOMATIC_DISCOUNTS, "list", filter],
		queryFn: () => automaticDiscountsApi.list(filter),
		enabled,
	});
}

export function useAutomaticDiscountKpis() {
	return useQuery({
		queryKey: [...KEY_AUTOMATIC_DISCOUNTS, "kpis"],
		queryFn: () => automaticDiscountsApi.getKpis(),
	});
}

export function useAutomaticDiscount(id: string | undefined) {
	return useQuery({
		queryKey: [...KEY_AUTOMATIC_DISCOUNTS, "detail", id],
		queryFn: () => automaticDiscountsApi.get(id!),
		enabled: Boolean(id),
	});
}

export function useCreateAutomaticDiscount() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (input: CreateAutomaticDiscountInput) =>
			automaticDiscountsApi.create(input),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_AUTOMATIC_DISCOUNTS });
		},
	});
}

export function useUpdateAutomaticDiscount() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string;
			data: UpdateAutomaticDiscountInput;
		}) => automaticDiscountsApi.update(id, data),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_AUTOMATIC_DISCOUNTS });
		},
	});
}

export function useToggleAutomaticDiscountStatus() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
			automaticDiscountsApi.toggleStatus(id, isActive),
		onSuccess: () => {
			void qc.invalidateQueries({ queryKey: KEY_AUTOMATIC_DISCOUNTS });
		},
	});
}
